<template>
    <div ref="zplPreviewRoot" class="zpl-preview">
        <!-- Controles de zoom -->
        <div class="zpl-controls" v-if="showControls">
            <button @click="zoomOut" :disabled="zoomLevel <= 0.5" class="control-btn">-</button>
            <span class="zoom-level">{{ Math.round(zoomLevel * 100) }}%</span>
            <button @click="zoomIn" :disabled="zoomLevel >= 3" class="control-btn">+</button>
            <button @click="resetZoom" class="control-btn">Reset</button>
            <button @click="exportToBase64" class="control-btn">Exportar</button>
        </div>

        <!-- Contenedor con scroll para zoom -->
        <div ref="zplPreviewContainer" class="zpl-preview-container" :style="containerStyle">
            <div class="canvas-wrapper" :style="canvasWrapperStyle">
                <canvas
                    ref="zplCanvas"
                    :key="canvasKey"
                    :width="canvasWidth"
                    :height="canvasHeight"
                    :style="canvasDisplayStyle"
                    class="zpl-canvas"
                ></canvas>
            </div>
        </div>

        <!-- Validación de errores -->
        <div v-if="validationErrors.length > 0" class="validation-errors">
            <h4>Errores de validación ZPL:</h4>
            <ul>
                <li v-for="(error, index) in validationErrors" :key="index" class="error-item">
                    {{ error }}
                </li>
            </ul>
        </div>
    </div>
</template>

<script>
import pako from 'pako'
import QRCode from 'qrcode'
import { parseZplTokens, resolveFDText as resolveFdField } from '@/utils/zplResolvedText.js'

const isZ64 = (s = '') => /^\s*:Z64:/i.test(s)
const isB64 = (s = '') => /^\s*:B64:/i.test(s)
const b64ToU8 = (b64) => Uint8Array.from(atob(b64), (c) => c.charCodeAt(0))

const ACS_SMALL = {
    G: 1,
    H: 2,
    I: 3,
    J: 4,
    K: 5,
    L: 6,
    M: 7,
    N: 8,
    O: 9,
    P: 10,
    Q: 11,
    R: 12,
    S: 13,
    T: 14,
    U: 15,
    V: 16,
    W: 17,
    X: 18,
    Y: 19
}

const ACS_BIG = {
    g: 20,
    h: 40,
    i: 60,
    j: 80,
    k: 100,
    l: 120,
    m: 140,
    n: 160,
    o: 180,
    p: 200,
    q: 220,
    r: 240,
    s: 260,
    t: 280,
    u: 300,
    v: 320,
    w: 340,
    x: 360,
    y: 380,
    z: 400
}

const cleanZplData = (str) => {
    return (str || '').replace(/[\r\n\s]/g, '')
}

const parseZplCoord = (value) => {
    const n = parseFloat(value)
    return Number.isFinite(n) ? n : 0
}

/** Máximo en dots para coords, extents y ^PW/^LL en preview (~40" @ 203 dpi). */
const MAX_ZPL_PREVIEW_DOTS = 8192

/** Máximo en píxeles del bitmap del canvas (memoria / límites del navegador). */
const MAX_ZPL_CANVAS_PX = 8192
const MAX_ZPL_FIT_CONTAINER_WIDTH = 640

/** Límites ^GF: bitmap 1 bit/px y ImageData RGBA en preview (anti-DoS) */
const MAX_GF_ROW_BYTES = Math.ceil(MAX_ZPL_PREVIEW_DOTS / 8)
const MAX_GF_TOTAL_BYTES = MAX_GF_ROW_BYTES * MAX_ZPL_PREVIEW_DOTS
const MAX_GF_COMPRESSED_BYTES = 2 * 1024 * 1024
const MAX_GF_IMAGE_PIXELS = 8 * 1024 * 1024
const MAX_GF_RGBA_BYTES = MAX_GF_IMAGE_PIXELS * 4
const MAX_GF_ACS_INPUT_CHARS = MAX_GF_COMPRESSED_BYTES

/**
 * Valida dimensiones ^GF antes de decodificar o reservar ImageData.
 * @returns {{ width: number, height: number }}
 */
const validateGfBitmapDimensions = (rowBytes, totalBytes) => {
    const rb = Number(rowBytes)
    const tb = Number(totalBytes)
    if (!Number.isFinite(rb) || !Number.isFinite(tb) || rb <= 0 || tb <= 0) {
        throw new Error('^GF: dimensiones inválidas')
    }
    if (rb > MAX_GF_ROW_BYTES) {
        throw new Error(`^GF: ancho de fila fuera de límite (máx. ${MAX_GF_ROW_BYTES} bytes)`)
    }
    if (tb > MAX_GF_TOTAL_BYTES) {
        throw new Error(`^GF: tamaño total fuera de límite (máx. ${MAX_GF_TOTAL_BYTES} bytes)`)
    }
    const width = rb * 8
    const height = Math.ceil(tb / rb)
    if (width > MAX_ZPL_PREVIEW_DOTS || height > MAX_ZPL_PREVIEW_DOTS) {
        throw new Error('^GF: dimensiones de imagen fuera de límite')
    }
    const pixels = width * height
    if (pixels > MAX_GF_IMAGE_PIXELS) {
        throw new Error('^GF: imagen demasiado grande para preview')
    }
    return { width, height }
}

const clampGfTotalBytes = (totalBytes) => {
    const tb = Number(totalBytes)
    if (!Number.isFinite(tb) || tb <= 0) return 0
    return Math.min(Math.floor(tb), MAX_GF_TOTAL_BYTES)
}

const clampGfRowBytes = (rowBytes) => {
    const rb = Number(rowBytes)
    if (!Number.isFinite(rb) || rb <= 0) return 0
    return Math.min(Math.floor(rb), MAX_GF_ROW_BYTES)
}

/** Valida que createImageData(width, height) no supere el tope RGBA del preview. */
const assertGfSafeForRgbaAlloc = (width, height) => {
    const w = Number(width)
    const h = Number(height)
    if (!Number.isFinite(w) || !Number.isFinite(h) || w <= 0 || h <= 0) {
        throw new Error('^GF: dimensiones de imagen inválidas')
    }
    if (w > MAX_ZPL_PREVIEW_DOTS || h > MAX_ZPL_PREVIEW_DOTS) {
        throw new Error('^GF: dimensiones de imagen fuera de límite')
    }
    const pixels = w * h
    if (pixels > MAX_GF_IMAGE_PIXELS || pixels * 4 > MAX_GF_RGBA_BYTES) {
        throw new Error('^GF: ImageData demasiado grande para preview')
    }
}

/**
 * Valida y normaliza parámetros ^GF antes de decodificar o reservar memoria.
 * @returns {{ rowBytes: number, totalBytes: number, width: number, height: number }}
 */
const prepareGfDecode = (rowBytes, totalBytes) => {
    const rb = clampGfRowBytes(rowBytes)
    const tb = clampGfTotalBytes(totalBytes)
    const dims = validateGfBitmapDimensions(rb, tb)
    assertGfSafeForRgbaAlloc(dims.width, dims.height)
    return { rowBytes: rb, totalBytes: tb, width: dims.width, height: dims.height }
}

const assertGfCompressedPayloadSize = (byteLength, label) => {
    if (byteLength > MAX_GF_COMPRESSED_BYTES) {
        throw new Error(`${label}: payload demasiado grande`)
    }
}

const assertGfDecodedByteLength = (length, maxBytes) => {
    const max = maxBytes > 0 && maxBytes <= MAX_GF_TOTAL_BYTES ? maxBytes : MAX_GF_TOTAL_BYTES
    if (length > max) {
        throw new Error('^GF: datos decodificados superan el límite permitido')
    }
}

/**
 * Descompresión zlib con tope de bytes de salida (pako@2.1.0 no soporta maxOutputLength).
 * Usa Inflate por chunks y aborta antes de acumular más de maxOut bytes.
 */
const inflateWithByteLimit = (compressed, maxOut, label = '^GF Z64') => {
    if (!maxOut || maxOut <= 0) {
        throw new Error(`${label}: límite de descompresión inválido`)
    }

    const chunkSize = Math.min(65536, maxOut)
    const chunks = []
    let total = 0

    const inflator = new pako.Inflate({ chunkSize })

    inflator.onData = (chunk) => {
        const next = total + chunk.length
        if (next > maxOut) {
            throw new Error(`${label}: descompresión superó el límite permitido`)
        }
        total = next
        chunks.push(chunk)
    }

    const ok = inflator.push(compressed, true)
    if (!ok || inflator.err) {
        throw new Error(inflator.msg || `${label}: datos comprimidos inválidos`)
    }

    if (total === 0) {
        return new Uint8Array(0)
    }
    if (chunks.length === 1) {
        return chunks[0]
    }

    const out = new Uint8Array(total)
    let offset = 0
    for (const chunk of chunks) {
        out.set(chunk, offset)
        offset += chunk.length
    }
    return out
}

const clampZplDots = (value) => {
    const n = Number(value)
    if (!Number.isFinite(n) || n < 0) return 0
    return Math.min(n, MAX_ZPL_PREVIEW_DOTS)
}

/** Igual que clampZplDots pero permite offset negativo (^LH). */
const clampZplCoordSigned = (value) => {
    const n = parseZplCoord(value)
    return Math.max(-MAX_ZPL_PREVIEW_DOTS, Math.min(n, MAX_ZPL_PREVIEW_DOTS))
}

const toEstInt = (v, def = 0) => {
    const n = parseInt(v, 10)
    return Number.isFinite(n) ? n : def
}

const orientedSize = (width, height, orient) => {
    const o = (orient || 'N').toUpperCase()
    if (o === 'R' || o === 'B') {
        return { width: height, height: width }
    }
    return { width, height }
}

const mergeExtents = (maxX, maxY, absX, absY, width, height, originMode = 'FO') => {
    const w = Math.max(0, width)
    const h = Math.max(0, height)
    const bottom = originMode === 'FT' ? absY : absY + h
    return {
        maxX: Math.max(maxX, clampZplDots(absX + w)),
        maxY: Math.max(maxY, clampZplDots(bottom))
    }
}

const estimateTextFieldBounds = (field, defaultFont) => {
    const font = field.font || defaultFont || { h: 20, w: 20 }
    const baseH = Math.max(1, toEstInt(font.h, 20))
    const baseW = Math.max(1, toEstInt(font.w, baseH))
    const scaleX = baseW / baseH
    const text = (field.text || '').trim()
    const fb = field.fb

    let width = 0
    const fbMaxWidth = fb ? toEstInt(fb.maxWidth, 0) : 0
    if (fbMaxWidth > 0) {
        width = fbMaxWidth
    } else if (text) {
        width = Math.ceil(text.length * baseH * 0.52 * scaleX)
    } else {
        width = baseW
    }

    const lineSpacing = fb ? toEstInt(fb.lineSpacing, 0) : 0
    const baselineStep = fb ? baseH + lineSpacing : baseH
    const declaredMaxLines = fb ? Math.max(1, toEstInt(fb.maxLines, 1)) : 1

    let renderedLines = 1
    if (text && fbMaxWidth > 0) {
        renderedLines = 0
        let lineLen = 0
        for (const word of text.split(/\s+/).filter(Boolean)) {
            const wordW = Math.ceil(word.length * baseH * 0.52 * scaleX)
            const gap = Math.ceil(baseH * 0.25)
            if (lineLen > 0 && lineLen + gap + wordW > fbMaxWidth) {
                renderedLines++
                lineLen = wordW
            } else {
                lineLen = lineLen > 0 ? lineLen + gap + wordW : wordW
            }
        }
        renderedLines = Math.max(1, renderedLines + 1)
        renderedLines = Math.min(renderedLines, declaredMaxLines)
    }

    const occupiedLineCount = Math.max(renderedLines, declaredMaxLines)
    let height = baseH
    if (field.originMode === 'FT') {
        height = occupiedLineCount * baselineStep
    } else if (renderedLines > 1) {
        height = (renderedLines - 1) * baselineStep + baseH
    }

    return { width, height }
}

const estimateBarcodeBounds = (field, moduleWidth) => {
    const barcode = field.barcode || {}
    const h = Math.max(1, toEstInt(barcode.height, 50))
    const text = field.text || ''
    const n = Math.max(text.length, 6)
    const modules = 11 * n + 35
    const mw = Math.max(1, toEstInt(moduleWidth, 2))
    const width = modules * mw
    const withLine = (barcode.printLine || 'Y').toUpperCase() !== 'N'
    const height = h + (withLine ? Math.round(h * 0.35) + 8 : 0)
    return { width, height }
}

const createEmptyEstimateField = (defaultFont) => ({
    type: 'text',
    originMode: 'FO',
    x: 0,
    y: 0,
    text: '',
    font: { ...defaultFont, orient: 'N' },
    fb: null,
    qrcode: null,
    barcode: null,
    image: null
})

/**
 * Estima el bounding box del contenido ZPL en dots (sin escala de pantalla).
 * Útil cuando ^LL/^PW son menores que el dibujo real.
 * @param {string} zplText
 * @param {Object} [values]
 * @param {Object} [params]
 */
const estimateContentExtents = (zplText, values = {}, params = {}) => {
    let maxX = 0
    let maxY = 0
    if (!zplText || typeof zplText !== 'string') {
        return { maxX, maxY }
    }

    const defaultFont = { name: '0', orient: 'N', h: 20, w: 20 }
    let labelHomeX = 0
    let labelHomeY = 0
    let currentPosition = { x: 0, y: 0 }
    let currentOrientation = 'N'
    let moduleWidth = 2
    let field = null

    const absPos = (x, y) => ({
        x: clampZplDots(labelHomeX + clampZplDots(x)),
        y: clampZplDots(labelHomeY + clampZplDots(y))
    })

    const includeFieldRect = (f, width, height) => {
        const pos = absPos(f.x, f.y)
        const orient =
            (f.font && f.font.orient) ||
            (f.qrcode && f.qrcode.orient) ||
            (f.barcode && f.barcode.orient) ||
            currentOrientation
        const sized = orientedSize(width, height, orient)
        const merged = mergeExtents(maxX, maxY, pos.x, pos.y, sized.width, sized.height, f.originMode)
        maxX = merged.maxX
        maxY = merged.maxY
    }

    const flushField = () => {
        if (!field) return
        if (field.type === 'text' && field.text) {
            const bounds = estimateTextFieldBounds(field, defaultFont)
            includeFieldRect(field, bounds.width, bounds.height)
        } else if (field.type === 'qrcode' && field.text && field.qrcode) {
            const mag = Math.max(1, toEstInt(field.qrcode.magnification, 2))
            const qrSize = 25 * 0.85 * mag
            includeFieldRect(field, qrSize, qrSize)
        } else if (field.type === 'barcode' && field.barcode) {
            const bounds = estimateBarcodeBounds(field, moduleWidth)
            includeFieldRect(field, bounds.width, bounds.height)
        } else if (field.type === 'image' && field.image) {
            includeFieldRect(field, field.image.width, field.image.height)
        }
        field = null
    }

    const ensureField = () => {
        if (!field) {
            field = createEmptyEstimateField(defaultFont)
        }
    }

    for (const item of parseZplTokens(zplText)) {
        if (maxX >= MAX_ZPL_PREVIEW_DOTS && maxY >= MAX_ZPL_PREVIEW_DOTS) {
            break
        }

        const cmd = item.command.toUpperCase()
        const p = item.params || []

        switch (cmd) {
            case '^LH':
                labelHomeX = clampZplCoordSigned(p[0])
                labelHomeY = clampZplCoordSigned(p[1])
                break

            case '^FO':
                flushField()
                currentPosition = {
                    x: clampZplDots(parseZplCoord(p[0])),
                    y: clampZplDots(parseZplCoord(p[1]))
                }
                field = createEmptyEstimateField(defaultFont)
                Object.assign(field, { originMode: 'FO', x: currentPosition.x, y: currentPosition.y })
                break

            case '^FT':
                flushField()
                currentPosition = {
                    x: clampZplDots(parseZplCoord(p[0])),
                    y: clampZplDots(parseZplCoord(p[1]))
                }
                field = createEmptyEstimateField(defaultFont)
                Object.assign(field, { originMode: 'FT', x: currentPosition.x, y: currentPosition.y })
                break

            case '^A': {
                ensureField()
                const fw = p[0] || '0N'
                const name = fw.slice(0, -1) || '0'
                const orient = fw.slice(-1) || 'N'
                const h = toEstInt(p[1], defaultFont.h)
                const w = toEstInt(p[2], h)
                defaultFont.h = h
                defaultFont.w = w
                field.font = { name, orient, h, w }
                currentOrientation = orient
                break
            }

            case '^FB':
                ensureField()
                field.fb = {
                    maxWidth: toEstInt(p[0], 0),
                    maxLines: toEstInt(p[1], 1),
                    lineSpacing: toEstInt(p[2], 0),
                    alignment: (p[3] || 'L').toUpperCase(),
                    indent: toEstInt(p[4], 0)
                }
                break

            case '^BQ':
                ensureField()
                field.type = 'qrcode'
                field.qrcode = {
                    orient: (p[0] || 'N').toUpperCase(),
                    magnification: toEstInt(p[2], 2)
                }
                currentOrientation = field.qrcode.orient
                break

            case '^BC':
                ensureField()
                field.type = 'barcode'
                field.barcode = {
                    orient: (p[0] || 'N').toUpperCase(),
                    height: toEstInt(p[1], 50),
                    printLine: p[2] || 'Y'
                }
                currentOrientation = field.barcode.orient
                break

            case '^BY':
                moduleWidth = Math.max(1, toEstInt(p[0], 2))
                break

            case '^FD':
                ensureField()
                field.text =
                    (field.text || '') +
                    resolveFdField(p[0] != null ? String(p[0]) : '', values, params)
                break

            case '^GF': {
                ensureField()
                const rowBytes = clampGfRowBytes(toEstInt(p[3], 0))
                const totalBytes = clampGfTotalBytes(toEstInt(p[2], toEstInt(p[1], 0)))
                if (rowBytes <= 0 || totalBytes <= 0) break
                try {
                    const dims = validateGfBitmapDimensions(rowBytes, totalBytes)
                    field.type = 'image'
                    field.image = { width: dims.width, height: dims.height }
                    includeFieldRect(field, dims.width, dims.height)
                } catch (_e) {
                    break
                }
                field = null
                break
            }

            case '^GB': {
                const pos = absPos(currentPosition.x, currentPosition.y)
                const w = clampZplDots(parseZplCoord(p[0]))
                const h = clampZplDots(parseZplCoord(p[1]))
                const sized = orientedSize(w, h, currentOrientation)
                maxX = Math.max(maxX, clampZplDots(pos.x + sized.width))
                maxY = Math.max(maxY, clampZplDots(pos.y + sized.height))
                break
            }

            case '^GC': {
                const pos = absPos(currentPosition.x, currentPosition.y)
                const diameter = clampZplDots(parseZplCoord(p[0]))
                const sized = orientedSize(diameter, diameter, currentOrientation)
                maxX = Math.max(maxX, clampZplDots(pos.x + sized.width))
                maxY = Math.max(maxY, clampZplDots(pos.y + sized.height))
                break
            }

            case '^FS':
            case '^XZ':
            case '^XA':
                flushField()
                break

            default:
                break
        }
    }

    flushField()

    return { maxX: clampZplDots(maxX), maxY: clampZplDots(maxY) }
}

/**
 * Extrae ^PW (print width) y ^LL (label length) del código ZPL en dots.
 * @param {string} zplText - Código ZPL
 * @returns {{ pw: number|null, ll: number|null }}
 */
const parseZplDeclaredDimensions = (zplText) => {
    if (!zplText || typeof zplText !== 'string') {
        return { pw: null, ll: null }
    }
    const pwMatch = zplText.match(/\^PW\s*(\d+)/i)
    const llMatch = zplText.match(/\^LL\s*(\d+)/i)
    const pw = pwMatch && pwMatch[1] ? parseInt(pwMatch[1], 10) : null
    const ll = llMatch && llMatch[1] ? parseInt(llMatch[1], 10) : null
    return {
        pw: Number.isFinite(pw) ? clampZplDots(pw) : null,
        ll: Number.isFinite(ll) ? clampZplDots(ll) : null
    }
}

/**
 * Tipografía para el preview: Barlow Condensed (cargada en public/index.html).
 * Importante: usamos la MISMA fuente para medir y dibujar texto en canvas.
 */
const ZPL_PREVIEW_TEXT_FONT_WEIGHT = 600 // 400 normal, 500 medium, 600 semibold, 700 bold
const ZPL_PREVIEW_TEXT_FONT_FAMILY = '"Barlow Condensed", Arial, Helvetica, sans-serif'
const ZPL_PREVIEW_FONT_CSS_FAMILY = 'Barlow Condensed'

/**
 * Decodifica ^GFA con compresión ACS a un Uint8Array (1 bit = 1 píxel).
 * @param {string} data - campo data (lo que viene tras los 4 params)
 * @param {number} rowBytes - bytes por fila
 * @param {number} totalBytes - bytes totales
 * @returns {{width:number,height:number,buffer:Uint8Array}}
 */
const decodeGFA_ACS = (data, rowBytes, totalBytes) => {
    const cleaned = cleanZplData(data)
    if (cleaned.length > MAX_GF_ACS_INPUT_CHARS) {
        throw new Error('^GF ACS: payload demasiado grande')
    }
    const rowHexLen = rowBytes * 2
    const rows = []
    let curr = ''
    let i = 0

    const nextChar = () => cleaned[i++]

    while (i < cleaned.length && rows.length * rowBytes < totalBytes) {
        const ch = nextChar()

        if (!ch) break

        if (ch === ',') {
            while (curr.length < rowHexLen) curr += '0'
            rows.push(curr)
            curr = ''
            continue
        }

        if (ch === ':') {
            const prev = rows[rows.length - 1] || ''.padEnd(rowHexLen, '0')
            rows.push(prev)
            curr = ''
            continue
        }

        if (ACS_SMALL[ch] || ACS_BIG[ch]) {
            const count = ACS_SMALL[ch] || ACS_BIG[ch]
            const value = nextChar()
            if (!value) break
            curr += value.repeat(count)
        } else {
            curr += ch
        }

        if (curr.length === rowHexLen) {
            rows.push(curr)
            curr = ''
        } else if (curr.length > rowHexLen) {
            rows.push(curr.slice(0, rowHexLen))
            curr = curr.slice(rowHexLen)
        }
    }

    if (curr.length > 0) {
        while (curr.length < rowHexLen) curr += '0'
        rows.push(curr)
        curr = ''
    }

    const neededRows = Math.ceil(totalBytes / rowBytes)
    while (rows.length < neededRows) rows.push(''.padEnd(rowHexLen, '0'))
    if (rows.length > neededRows) rows.length = neededRows

    const out = new Uint8Array(totalBytes)
    let pos = 0

    for (const row of rows) {
        for (let j = 0; j < rowHexLen; j += 2) {
            if (pos >= totalBytes) break
            const byteHex = row.substr(j, 2)
            out[pos++] = parseInt(byteHex, 16) || 0
        }
    }

    return {
        width: rowBytes * 8,
        height: Math.ceil(totalBytes / rowBytes),
        buffer: out
    }
}

const decodeGFA_Z64 = (data, totalBytes) => {
    const maxOut = totalBytes
    const m = String(data).match(/:Z64:([\s\S]*?)(?::[0-9A-Fa-f]{4})?\s*$/)
    if (!m) throw new Error('Z64 mal formado')
    const b64 = m[1].replace(/\s+/g, '')
    if (b64.length > Math.ceil(MAX_GF_COMPRESSED_BYTES * 1.4)) {
        throw new Error('^GF Z64: payload demasiado grande')
    }
    const compressed = b64ToU8(b64)
    assertGfCompressedPayloadSize(compressed.length, '^GF Z64')
    const inflated = inflateWithByteLimit(compressed, maxOut, '^GF Z64')
    assertGfDecodedByteLength(inflated.length, maxOut)

    if (totalBytes && inflated.length !== totalBytes) {
        const out = new Uint8Array(totalBytes)
        out.set(inflated.subarray(0, Math.min(inflated.length, totalBytes)))
        return out
    }

    return inflated
}

const decodeGFA_B64 = (data, totalBytes) => {
    const maxOut = totalBytes
    const m = String(data).match(/:B64:([\s\S]*?)(?::[0-9A-Fa-f]{4})?\s*$/)
    if (!m) throw new Error('B64 mal formado')
    const b64 = m[1].replace(/\s+/g, '')
    if (b64.length > Math.ceil(MAX_GF_COMPRESSED_BYTES * 1.4)) {
        throw new Error('^GF B64: payload demasiado grande')
    }
    const raw = b64ToU8(b64)
    assertGfCompressedPayloadSize(raw.length, '^GF B64')
    assertGfDecodedByteLength(raw.length, maxOut)

    if (totalBytes && raw.length !== totalBytes) {
        const out = new Uint8Array(totalBytes)
        out.set(raw.subarray(0, Math.min(raw.length, totalBytes)))
        return out
    }

    return raw
}

export default {
    name: 'ZplPreview',
    props: {
        data: { type: String, required: true },
        density: { type: Number, default: 203 },
        width: { type: Number, default: 57 },
        height: { type: Number, default: 51 },
        showControls: { type: Boolean, default: false },
        values: { type: Object, default: () => ({}) },
        params: { type: Object, default: () => ({}) },
        /** Factor extra sobre el preview (además de previewScale); por defecto 1 */
        scale: { type: Number, default: 1 },
        fitToContainer: { type: Boolean, default: true },
        position: { type: String, default: 'top left' },
        /**
         * Escala visual tipo Labelary: lienzo ≈ PW×previewScale × LL×previewScale px; el dibujo sigue en coordenadas ZPL (dots).
         * No intenta tamaño físico real en pantalla.
         */
        previewScale: { type: Number, default: 0.9 },
        /**
         * Con 'zpl' el tamaño en dots viene de ^PW/^LL (fallback: mm×densidad→dots si faltan).
         * Con 'physical' solo mm×densidad→dots (sin leer PW/LL del texto).
         */
        canvasSizeMode: {
            type: String,
            default: 'zpl',
            validator: (v) => v === 'zpl' || v === 'physical'
        },
        /**
         * Suma en dots al alto y ancho de fuente del ^A solo en el canvas (no altera el ZPL).
         */
        fontDotBoost: { type: Number, default: 4.3 },
        /**
         * Margen interior en dots (misma unidad que ^FO). No cambia el tamaño del canvas:
         * el contenido se escala y desplaza para caber dentro de PW×LL con aire al borde.
         */
        previewInsetDots: { type: Number, default: 5 }
    },
    data() {
        return {
            canvas: null,
            ctx: null,
            zoomLevel: 1,
            state: null,
            validationErrors: [],
            canvasKey: 0,
            containerSize: { width: 0, height: 0 },
            resizeObserver: null
        }
    },
    computed: {
        /** previewScale × scale (prop): factor deseado antes de limitar el bitmap del canvas */
        effectiveRenderScale() {
            return this.previewScale * this.scale
        },
        /** Escala real de dibujo: effectiveRenderScale acotada para que el canvas no supere MAX_ZPL_CANVAS_PX */
        canvasRenderScale() {
            const base = this.effectiveRenderScale
            const w = this.labelDotsWidth
            const h = this.labelDotsHeight
            if (w <= 0 || h <= 0) return base
            const maxByW = MAX_ZPL_CANVAS_PX / w
            const maxByH = MAX_ZPL_CANVAS_PX / h
            return Math.min(base, maxByW, maxByH)
        },
        _physicalWidthMm() {
            const n = Number(this.width)
            return Number.isFinite(n) && n > 0 ? n : 57
        },
        _physicalHeightMm() {
            const n = Number(this.height)
            return Number.isFinite(n) && n > 0 ? n : 51
        },
        contentExtents() {
            return estimateContentExtents(this.data, this.values, this.params)
        },
        zplDeclaredDimensions() {
            return parseZplDeclaredDimensions(this.data)
        },
        _physicalDotsWidth() {
            return clampZplDots(Math.round((this._physicalWidthMm * this.density) / 25.4))
        },
        _physicalDotsHeight() {
            return clampZplDots(Math.round((this._physicalHeightMm * this.density) / 25.4))
        },
        /** Ancho/alto del canvas en dots: ^PW/^LL (o mm) y solo crece si el dibujo se sale */
        labelDotsWidth() {
            const physical = this._physicalDotsWidth
            const contentPad = 4
            const { maxX } = this.contentExtents
            const contentRight = maxX > 0 ? Math.ceil(maxX + contentPad) : 0

            if (this.canvasSizeMode === 'physical') {
                return clampZplDots(contentRight > physical ? contentRight : physical)
            }

            const { pw } = this.zplDeclaredDimensions
            const base = pw != null && pw > 0 ? pw : physical
            return clampZplDots(contentRight > base ? contentRight : base)
        },
        labelDotsHeight() {
            const physical = this._physicalDotsHeight
            const contentPad = 4
            const fontPad = Number.isFinite(Number(this.fontDotBoost)) ? Number(this.fontDotBoost) : 0
            const { maxY } = this.contentExtents
            const contentBottom = maxY > 0 ? Math.ceil(maxY + contentPad + fontPad) : 0

            if (this.canvasSizeMode === 'physical') {
                return clampZplDots(contentBottom > physical ? contentBottom : physical)
            }

            const { ll } = this.zplDeclaredDimensions
            const base = ll != null && ll > 0 ? ll : physical
            return clampZplDots(contentBottom > base ? contentBottom : base)
        },
        canvasWidth() {
            const px = Math.round(this.labelDotsWidth * this.canvasRenderScale)
            return Math.max(1, px)
        },
        canvasHeight() {
            const px = Math.round(this.labelDotsHeight * this.canvasRenderScale)
            return Math.max(1, px)
        },
        effectiveScale() {
            if (!this.fitToContainer) return 1
            const widthCanvas = this.canvasWidth
            if (!widthCanvas) return 1
            const containerWidth = this.containerSize.width > 0 ? this.containerSize.width : widthCanvas
            return containerWidth / widthCanvas
        },
        reservedVisualSize() {
            const visualScale = this.fitToContainer ? this.effectiveScale : this.zoomLevel
            return {
                width: Math.max(1, Math.ceil(this.canvasWidth * visualScale)),
                height: Math.max(1, Math.ceil(this.canvasHeight * visualScale))
            }
        },
        canvasDisplayStyle() {
            return {
                width: '100%',
                height: '100%',
                display: 'block'
            }
        },
        /** Inset pedido, limitado para que quede área interior positiva */
        effectiveInsetDots() {
            const n = Number(this.previewInsetDots)
            if (!Number.isFinite(n) || n <= 0) return 0
            const pw = this.labelDotsWidth
            const ll = this.labelDotsHeight
            const maxIn = Math.min(pw, ll) / 2 - 0.0001
            if (maxIn <= 0) return 0
            return Math.min(n, maxIn)
        },
        containerStyle() {
            if (this.fitToContainer) {
                const { height } = this.reservedVisualSize
                return {
                    boxSizing: 'border-box',
                    width: '100%',
                    maxWidth: '100%',
                    height: `${height}px`,
                    minHeight: `${height}px`,
                    margin: '0 auto',
                    overflow: 'hidden'
                }
            }
            return {
                width: 'auto',
                height: 'auto',
                maxWidth: '100%',
                overflow: 'auto'
            }
        },
        canvasWrapperStyle() {
            const visualScale = this.fitToContainer ? this.effectiveScale : this.zoomLevel
            return {
                width: `${this.canvasWidth}px`,
                height: `${this.canvasHeight}px`,
                transform: `scale(${visualScale})`,
                transformOrigin: this.fitToContainer ? this.position : 'top left',
                display: 'inline-block',
                flexShrink: '0'
            }
        }
    },
    mounted() {
        this.initCanvas()
        this._scheduleFontReadyRerender()
        this.renderZPL()
        if (this.fitToContainer) {
            this.$nextTick(() => {
                this.setupResizeObserver()
                this._remeasureContainer()
            })
        }
    },
    beforeDestroy() {
        this.teardownResizeObserver()
    },
    watch: {
        data() {
            this.$nextTick(() => this.renderZPL())
        },
        density() {
            this.$nextTick(() => {
                this.initCanvas()
                this.renderZPL()
            })
        },
        width() {
            this.$nextTick(() => {
                this.initCanvas()
                this.renderZPL()
            })
        },
        height() {
            this.$nextTick(() => {
                this.initCanvas()
                this.renderZPL()
            })
        },
        scale() {
            this.$nextTick(() => {
                this.initCanvas()
                this.renderZPL()
            })
        },
        previewScale() {
            this.$nextTick(() => {
                this.initCanvas()
                this.renderZPL()
            })
        },
        canvasSizeMode() {
            this.$nextTick(() => {
                this.initCanvas()
                this.renderZPL()
            })
        },
        fontDotBoost() {
            this.$nextTick(() => this.renderZPL())
        },
        previewInsetDots() {
            this.$nextTick(() => this.renderZPL())
        },
        values: {
            deep: true,
            handler() {
                this.canvasKey += 1
                this.$nextTick(() => {
                    this.initCanvas()
                    this.renderZPL()
                })
            }
        },
        params: {
            deep: true,
            handler() {
                this.canvasKey += 1
                this.$nextTick(() => {
                    this.initCanvas()
                    this.renderZPL()
                })
            }
        },
        fitToContainer: {
            handler(val) {
                this.$nextTick(() => {
                    if (val) {
                        this.setupResizeObserver()
                        this._remeasureContainer()
                    } else {
                        this.teardownResizeObserver()
                    }
                })
            }
        }
    },
    methods: {
        _remeasureContainer() {
            const measureEl = this.getMeasureTarget()
            if (measureEl) this.updateContainerSize(measureEl)
            const self = this
            requestAnimationFrame(function () {
                const el = self.getMeasureTarget()
                if (el) self.updateContainerSize(el)
            })
        },
        _scheduleFontReadyRerender() {
            try {
                if (typeof document === 'undefined' || !document.fonts || !document.fonts.ready || typeof document.fonts.ready.then !== 'function') return
                document.fonts.ready.then(() => {
                    if (this.state && this.state._measureCache) this.state._measureCache.clear()
                    this.$nextTick(() => this.renderZPL())
                })
            } catch (_e) {
                // ignore
            }
        },

        async _ensureFontsReady() {
            try {
                if (typeof document === 'undefined' || !document.fonts) return
                await document.fonts.load(`${ZPL_PREVIEW_TEXT_FONT_WEIGHT} 24px ${ZPL_PREVIEW_FONT_CSS_FAMILY}`)
            } catch (_e) {
                // ignore
            }
        },
        getMeasureTarget() {
            const root = this.$refs.zplPreviewRoot
            if (!root) return null

            let el = root.parentElement
            let cardContainer = null
            let popup = null
            let fallback = null

            while (el) {
                if (el.matches && el.matches('.full-preview-content, .kind-of.zpl-preview')) {
                    cardContainer = el
                }
                if (el.classList && el.classList.contains('popup')) {
                    const popupW = el.clientWidth || 0
                    if (popupW > 0) {
                        popup = el
                    }
                }
                if (!fallback && (el.clientWidth || 0) > 0 && !(el.classList && el.classList.contains('full-preview'))) {
                    fallback = el
                }
                el = el.parentElement
            }

            if (cardContainer) return cardContainer
            if (popup) return popup
            return fallback || this.$refs.zplPreviewContainer || root
        },

        _getFallbackMeasureSize(el) {
            if (!el || !el.matches) return null

            if (el.matches('.full-preview-content, .kind-of.zpl-preview, .popup.labelPreview, .popup.zplPreview')) {
                const vw = typeof window !== 'undefined' ? window.innerWidth : MAX_ZPL_FIT_CONTAINER_WIDTH
                const vh = typeof window !== 'undefined' ? window.innerHeight : MAX_ZPL_FIT_CONTAINER_WIDTH
                return {
                    width: Math.min(Math.round(vw * 0.9), MAX_ZPL_FIT_CONTAINER_WIDTH),
                    height: Math.min(Math.round(vh * 0.75), Math.round(MAX_ZPL_FIT_CONTAINER_WIDTH * 1.25))
                }
            }

            return null
        },

        _resolveMeasureSize(el) {
            if (!el) return { width: 0, height: 0 }

            let width = el.clientWidth || 0
            let height = el.clientHeight || 0

            if (width <= 0) {
                const fallbackSize = this._getFallbackMeasureSize(el)
                if (fallbackSize) {
                    width = fallbackSize.width
                    height = fallbackSize.height || height
                }
            }

            if (width <= 0) {
                let node = el.parentElement
                while (node) {
                    if (node.classList && node.classList.contains('full-preview')) {
                        break
                    }
                    width = node.clientWidth || 0
                    height = node.clientHeight || 0
                    if (width > 0) break
                    node = node.parentElement
                }
            }

            if (el.matches && el.matches('.full-preview-content, .kind-of.zpl-preview, .popup.labelPreview, .popup.zplPreview')) {
                width = Math.min(width, MAX_ZPL_FIT_CONTAINER_WIDTH)
            }

            return { width, height }
        },

        setupResizeObserver() {
            const el = this.getMeasureTarget()
            if (!el || typeof ResizeObserver === 'undefined') {
                if (el) this.updateContainerSize(el)
                return
            }
            this.teardownResizeObserver()
            this.resizeObserver = new ResizeObserver((entries) => {
                const entry = entries[0]
                if (entry && entry.target) this.updateContainerSize(entry.target)
            })
            this.resizeObserver.observe(el)
            this.$nextTick(() => this.updateContainerSize(el))
        },

        teardownResizeObserver() {
            if (this.resizeObserver) {
                this.resizeObserver.disconnect()
                this.resizeObserver = null
            }
        },

        updateContainerSize(el) {
            if (!el) return
            const { width, height } = this._resolveMeasureSize(el)
            if (this.containerSize.width !== width || this.containerSize.height !== height) {
                this.containerSize = { width, height }
            }
        },

        /**
         * Dado un nombre base y sufijos tipo _day, _time, _timestamp, _d1.._d7,
         * devuelve el valor calculado desde params[base].
         */
        resolveFDText(fdRaw) {
            return resolveFdField(fdRaw, this.values, this.params)
        },

        initCanvas() {
            this.canvas = this.$refs.zplCanvas
            if (!this.canvas) return
            this.ctx = this.canvas.getContext('2d')
            this.canvas.width = this.canvasWidth
            this.canvas.height = this.canvasHeight
            this.ctx.fillStyle = '#ffffff'
            this.ctx.fillRect(0, 0, this.canvasWidth, this.canvasHeight)
        },

        async renderZPL() {
            if (!this.ctx) return

            this.validationErrors = []
            this.ctx.setTransform(1, 0, 0, 1, 0, 0)

            if (this.canvas.width !== this.canvasWidth || this.canvas.height !== this.canvasHeight) {
                this.canvas.width = this.canvasWidth
                this.canvas.height = this.canvasHeight
            }

            this.ctx.clearRect(0, 0, this.canvasWidth, this.canvasHeight)
            this.ctx.fillStyle = '#fff'
            this.ctx.fillRect(0, 0, this.canvasWidth, this.canvasHeight)

            const zplText = (this.data || '').trim()
            if (!zplText) return

            await this._ensureFontsReady()

            const parsed = this.parseZPL(zplText)
            await this.executeZPL(parsed)
        },

        parseZPL(zplText) {
            return parseZplTokens(zplText)
        },

        resetLabelState() {
            this.state = {
                dpi: this.density,
                labelHome: { x: 0, y: 0 },
                pw: null,
                ll: null,
                invert: false,
                reverse: false,
                charset: 'utf-8',
                hexEscape: null,
                font: {
                    name: '0',
                    orient: 'N',
                    h: 20,
                    w: 20
                },
                field: null,
                currentPosition: { x: 0, y: 0 },
                currentOrientation: 'N',
                _measureCache: new Map()
            }
        },

        _toInt(v, def = 0) {
            const n = parseInt(v, 10)
            return Number.isFinite(n) ? n : def
        },

        _zplTextFontCss(fontPx) {
            return `${ZPL_PREVIEW_TEXT_FONT_WEIGHT} ${fontPx}px ${ZPL_PREVIEW_TEXT_FONT_FAMILY}`
        },

        _measureText(text, fontPx) {
            const key = `${fontPx}|${ZPL_PREVIEW_TEXT_FONT_WEIGHT}|${ZPL_PREVIEW_TEXT_FONT_FAMILY}|${text}`
            if (this.state._measureCache.has(key)) return this.state._measureCache.get(key)
            const ctx = this.ctx
            ctx.save()
            ctx.setTransform(1, 0, 0, 1, 0, 0)
            ctx.font = this._zplTextFontCss(fontPx)
            const w = ctx.measureText(text).width
            ctx.restore()
            this.state._measureCache.set(key, w)
            return w
        },

        _withOrientationDraw(x, y, orient, drawFn) {
            const ctx = this.ctx
            ctx.save()
            ctx.translate(x, y)
            const map = { N: 0, R: 90, I: 180, B: 270 }
            const deg = map[orient] != null ? map[orient] : 0
            ctx.rotate((deg * Math.PI) / 180)
            drawFn(ctx)
            ctx.restore()
        },

        _ensureField() {
            if (!this.state.field) {
                this.state.field = {
                    type: 'text',
                    originMode: 'FO',
                    x: 0,
                    y: 0,
                    text: '',
                    haveData: false,
                    font: { ...this.state.font },
                    fb: null,
                    fhEscape: this.state.hexEscape,
                    reverse: false
                }
            }

            // Si hay un reverse pendiente, aplicarlo al campo y desactivarlo
            if (this.state.reverse) {
                this.state.field.reverse = true
                this.state.reverse = false
            }
        },

        _flushField() {
            const f = this.state.field
            if (!f) return
            if (f.type === 'text' && f.haveData) this._drawTextField(f)
            if (f.type === 'qrcode' && f.haveData) this._drawQRCode(f)
            // type image se dibuja inmediatamente en ^GF (ya que conocemos el bitmap)
            this.state.field = null
        },

        _applyHexEscapes(text, escapeChar) {
            if (!escapeChar) return text
            const esc = escapeChar.replace(/[-/\\^$*+?.()|[\]{}]/g, '\\$&')
            const re = new RegExp(`${esc}([0-9A-Fa-f]{2})`, 'g')
            return text.replace(re, (_, hh) => String.fromCharCode(parseInt(hh, 16)))
        },

        _applyAlignmentOffset(lineWidth, maxWidth, align) {
            if (!maxWidth) return 0
            if (align === 'C') return (maxWidth - lineWidth) / 2
            if (align === 'R') return maxWidth - lineWidth
            return 0
        },

        _wrapTextForFB(text, fontPx, fb) {
            if (!fb || !fb.maxWidth || fb.maxWidth <= 0) return [text]

            const trimmed = (text || '').trim()
            const words = trimmed.split(/\s+/).filter(Boolean)
            const lines = []
            let line = ''

            for (const w of words) {
                const test = line ? `${line} ${w}` : w
                const testW = this._measureText(test, fontPx)
                if (testW <= fb.maxWidth || !line) line = test
                else {
                    lines.push(line)
                    line = w
                }
            }

            if (line) lines.push(line)
            if (fb.maxLines && lines.length > fb.maxLines) return lines.slice(0, fb.maxLines)

            return lines
        },

        /**
         * Paso vertical entre baselines en bloques ^FB: alto nominal del ^A (dots) + espacio extra del ^FB.
         * No usa fontDotBoost: el ZPL coloca campos siguientes según ese grid; el boost solo engruesa el dibujo.
         */
        _fbBaselineStepDots(baseH, fbLineSpacingExtra) {
            const h = Math.max(1, this._toInt(baseH, 20))
            const extra = this._toInt(fbLineSpacingExtra, 0)
            return h + extra
        },

        _drawTextField(f) {
            const boost = typeof this.fontDotBoost === 'number' && Number.isFinite(this.fontDotBoost) ? this.fontDotBoost : 0
            const baseH = this._toInt(f.font.h, 20)
            const baseW = Math.max(this._toInt(f.font.w, baseH), 1)
            const fontH = Math.max(0.5, baseH + boost)
            const fontW = Math.max(0.5, baseW + boost)
            const scaleX = fontW / fontH
            const text = this._applyHexEscapes(f.text, f.fhEscape)

            let lines = [text]
            let lineSpacing = 0
            let fbAlign = 'L'
            let maxWidth = null

            if (f.fb) {
                maxWidth = this._toInt(f.fb.maxWidth, 0)
                lineSpacing = this._toInt(f.fb.lineSpacing, 0)
                fbAlign = (f.fb.alignment || 'L').toUpperCase()
                lines = this._wrapTextForFB(text, fontH, { ...f.fb, maxWidth })
            }

            /** Paso vertical en dots según ZPL (^A alto nominal + ^FB espacio extra); sin fontDotBoost */
            const baselineStepDots = f.fb ? this._fbBaselineStepDots(baseH, lineSpacing) : baseH + lineSpacing

            // ^FB declara un máximo de líneas: aunque el texto use menos, el bloque ocupa ese alto.
            // Esto afecta directamente a ^FT (baseline anclada a la última línea posible del bloque).
            const declaredMaxLines = f.fb ? Math.max(1, this._toInt(f.fb.maxLines, 1)) : 1
            const occupiedLineCount = Math.max(lines.length, declaredMaxLines)

            const absX = this.state.labelHome.x + f.x
            const absY = this.state.labelHome.y + f.y

            const drawLines = (ctx2) => {
                ctx2.save()
                ctx2.font = this._zplTextFontCss(fontH)
                ctx2.textBaseline = 'alphabetic'

                // Usar color blanco si reverse está activo en el campo, negro en caso contrario
                const textColor = f.reverse ? '#ffffff' : '#000000'

                for (let i = 0; i < lines.length; i++) {
                    const l = lines[i]
                    const lw = this._measureText(l, fontH) * scaleX
                    const ox = this._applyAlignmentOffset(lw, maxWidth, fbAlign)
                    const localX = ox
                    const adjustY = 0.75

                    // Calcular la posición Y correcta para cada línea
                    let localY
                    if (f.originMode === 'FT') {
                        // ^FT: baseline anclada a la última línea posible del bloque (respeta maxLines de ^FB)
                        const last = occupiedLineCount - 1
                        localY = (i - last) * baselineStepDots
                    } else {
                        // ^FO: origen arriba-izquierda; al crecer la fuente el bloque crece hacia abajo.
                        if (lines.length > 1) {
                            localY = i * baselineStepDots + baseH * adjustY
                        } else {
                            localY = baseH * adjustY
                        }
                    }

                    ctx2.save()
                    ctx2.scale(scaleX, 1)
                    ctx2.fillStyle = textColor
                    ctx2.fillText(l, localX / scaleX, localY)
                    ctx2.restore()
                }

                ctx2.restore()
            }

            this._withOrientationDraw(absX, absY, f.font.orient, drawLines)
        },

        _drawQRCode(f) {
            if (!f.text || !f.qrcode) return

            const { orient, magnification } = f.qrcode
            const text = f.text

            // Calcular tamaño del QR code
            // En ZPL, el tamaño se calcula basado en la magnification
            // magnification 1 = ~25x25 módulos, cada módulo ~3 dots a 203 DPI
            // Para simplificar, usamos un tamaño base y lo escalamos
            const baseSize = 25 // Tamaño base en módulos
            const moduleSize = 0.85 * magnification // Tamaño de cada módulo en dots
            const qrSize = baseSize * moduleSize // Tamaño total aproximado

            // Calcular posición absoluta
            const absX = this.state.labelHome.x + f.x
            const absY = this.state.labelHome.y + f.y

            // Generar QR code usando una librería
            this._generateQRCode(text, qrSize)
                .then((qrCanvas) => {
                    if (qrCanvas) {
                        // Dibujar el QR code en el canvas principal con orientación
                        this._withOrientationDraw(absX, absY, orient, (ctx2) => {
                            ctx2.drawImage(qrCanvas, 0, 0, qrSize, qrSize)
                        })
                    } else {
                        // Fallback: dibujar un placeholder si no hay librería disponible
                        this._withOrientationDraw(absX, absY, orient, (ctx2) => {
                            ctx2.fillStyle = '#cccccc'
                            ctx2.fillRect(0, 0, qrSize, qrSize)
                            ctx2.strokeStyle = '#000000'
                            ctx2.strokeRect(0, 0, qrSize, qrSize)
                            ctx2.fillStyle = '#000000'
                            ctx2.font = this._zplTextFontCss(12)
                            ctx2.textAlign = 'center'
                            ctx2.textBaseline = 'middle'
                            ctx2.fillText('QR', qrSize / 2, qrSize / 2)
                        })
                    }
                })
                .catch((error) => {
                    logError('Error drawing QR code:', error)
                    // Dibujar placeholder en caso de error
                    this._withOrientationDraw(absX, absY, orient, (ctx2) => {
                        ctx2.fillStyle = '#cccccc'
                        ctx2.fillRect(0, 0, qrSize, qrSize)
                        ctx2.strokeStyle = '#000000'
                        ctx2.strokeRect(0, 0, qrSize, qrSize)
                    })
                })
        },

        async _generateQRCode(text, size) {
            // Generar QR code localmente usando la librería qrcode
            try {
                // Generar QR code usando la librería qrcode
                // QRCode.toCanvas puede recibir un canvas o crear uno nuevo
                const canvas = await QRCode.toCanvas(text, {
                    width: size,
                    margin: 0,
                    color: {
                        dark: '#000000',
                        light: '#FFFFFF'
                    }
                })

                return canvas
            } catch (error) {
                logError('Error generating QR code:', error)
                return null
            }
        },

        async _drawGFField(field) {
            const { data, width, height, rowBytes, totalBytes } = field.image

            if (!width || !height || !rowBytes || !totalBytes) {
                this.validationErrors.push('^GF: dimensiones inválidas')
                return
            }

            try {
                // 1) Decodificar a bytes (1 bit = 1 px); límites ya validados en executeZPL (^GF)
                let bytes

                if (isZ64(data)) {
                    bytes = decodeGFA_Z64(data, totalBytes)
                } else if (isB64(data)) {
                    bytes = decodeGFA_B64(data, totalBytes)
                } else {
                    bytes = decodeGFA_ACS(data, rowBytes, totalBytes).buffer
                }

                assertGfDecodedByteLength(bytes.length, totalBytes)
                assertGfSafeForRgbaAlloc(width, height)

                // 2) Volcar a ImageData RGBA
                const img = this.ctx.createImageData(width, height)
                const dst = img.data
                let px = 0

                for (let i = 0; i < bytes.length; i++) {
                    const b = bytes[i]
                    for (let bit = 7; bit >= 0; bit--) {
                        if (px >= width * height) break
                        const on = (b >> bit) & 1
                        const off = px * 4
                        const v = on ? 0 : 255
                        dst[off] = v
                        dst[off + 1] = v
                        dst[off + 2] = v
                        dst[off + 3] = 255
                        px++
                    }
                }

                // putImageData ignora transforms; drawImage sí las respeta.
                const scratch = document.createElement('canvas')
                scratch.width = width
                scratch.height = height
                scratch.getContext('2d').putImageData(img, 0, 0)

                const absX = this.state.labelHome.x + field.x
                const absY = this.state.labelHome.y + field.y

                this._withOrientationDraw(absX, absY, field.font.orient, (ctx2) => {
                    ctx2.drawImage(scratch, 0, 0, width, height)
                })
            } catch (err) {
                const msg = err && err.message ? err.message : String(err)
                this.validationErrors.push(`^GF: ${msg}`)
            }
        },

        _drawGBBox(x, y, width, height, thickness, color, cornerRadius, orientation) {
            if (!this.ctx) return

            const w = Math.max(0, this._toInt(width, 0))
            const h = Math.max(0, this._toInt(height, 0))
            const t = Math.max(1, this._toInt(thickness, 1))
            const c = (color || 'B').toUpperCase()
            let r = Math.max(0, this._toInt(cornerRadius, 0))
            const fillColor = c === 'W' ? '#ffffff' : '#000000'
            const orient = orientation || 'N'

            // Si no hay dimensiones válidas, no dibujar
            if (w <= 0 || h <= 0) return

            // Limitar el radio de esquinas redondeadas al máximo permitido
            if (r > 0) {
                const maxRadius = Math.min(w, h) / 2
                r = Math.min(r, maxRadius)
            }

            // Si el grosor es mayor o igual a las dimensiones, dibujar un rectángulo relleno
            const isFilled = t >= w || t >= h

            // Calcular la posición absoluta
            const absX = this.state.labelHome.x + (x || 0)
            const absY = this.state.labelHome.y + (y || 0)

            this._withOrientationDraw(absX, absY, orient, (ctx2) => {
                ctx2.save()
                ctx2.fillStyle = fillColor
                ctx2.strokeStyle = fillColor
                ctx2.lineCap = 'square'
                ctx2.lineJoin = 'miter'

                if (isFilled) {
                    // Si el grosor es mayor que las dimensiones, dibujar un rectángulo relleno
                    if (r > 0) {
                        ctx2.beginPath()
                        ctx2.moveTo(r, 0)
                        ctx2.lineTo(w - r, 0)
                        ctx2.quadraticCurveTo(w, 0, w, r)
                        ctx2.lineTo(w, h - r)
                        ctx2.quadraticCurveTo(w, h, w - r, h)
                        ctx2.lineTo(r, h)
                        ctx2.quadraticCurveTo(0, h, 0, h - r)
                        ctx2.lineTo(0, r)
                        ctx2.quadraticCurveTo(0, 0, r, 0)
                        ctx2.closePath()
                        ctx2.fill()
                    } else {
                        ctx2.fillRect(0, 0, w, h)
                    }
                } else {
                    // Dibujar borde grueso hacia el interior
                    ctx2.lineWidth = t

                    // Ajustar posición y tamaño para que el borde se dibuje completamente hacia adentro
                    // El stroke se dibuja centrado en la línea, así que necesitamos ajustar la posición
                    const halfT = t / 2
                    const innerW = w - t
                    const innerH = h - t

                    if (r > 0) {
                        // Ajustar el radio para el rectángulo interior
                        const innerR = Math.max(0, r - halfT)

                        // Rectángulo con esquinas redondeadas - dibujar desde (halfT, halfT) para que el borde quede hacia adentro
                        ctx2.beginPath()
                        ctx2.moveTo(halfT + innerR, halfT)
                        ctx2.lineTo(halfT + innerW - innerR, halfT)
                        ctx2.quadraticCurveTo(halfT + innerW, halfT, halfT + innerW, halfT + innerR)
                        ctx2.lineTo(halfT + innerW, halfT + innerH - innerR)
                        ctx2.quadraticCurveTo(halfT + innerW, halfT + innerH, halfT + innerW - innerR, halfT + innerH)
                        ctx2.lineTo(halfT + innerR, halfT + innerH)
                        ctx2.quadraticCurveTo(halfT, halfT + innerH, halfT, halfT + innerH - innerR)
                        ctx2.lineTo(halfT, halfT + innerR)
                        ctx2.quadraticCurveTo(halfT, halfT, halfT + innerR, halfT)
                        ctx2.closePath()
                        ctx2.stroke()
                    } else {
                        // Rectángulo simple con borde hacia el interior - dibujar desde (halfT, halfT)
                        ctx2.strokeRect(halfT, halfT, innerW, innerH)
                    }
                }

                ctx2.restore()
            })
        },

        async executeZPL(parsed) {
            const ctx = this.ctx
            const rs = this.canvasRenderScale

            ctx.setTransform(1, 0, 0, 1, 0, 0)
            ctx.clearRect(0, 0, ctx.canvas.width, ctx.canvas.height)
            ctx.fillStyle = '#ffffff'
            ctx.fillRect(0, 0, ctx.canvas.width, ctx.canvas.height)
            ctx.setTransform(rs, 0, 0, rs, 0, 0)

            const inset = this.effectiveInsetDots
            const pw = this.labelDotsWidth
            const ll = this.labelDotsHeight
            if (inset > 0) {
                const innerW = pw - 2 * inset
                const innerH = ll - 2 * inset
                if (innerW > 0 && innerH > 0) {
                    const sx = innerW / pw
                    const sy = innerH / ll
                    ctx.translate(inset, inset)
                    ctx.scale(sx, sy)
                }
            }

            this.resetLabelState()

            for (const item of parsed) {
                const cmd = item.command.toUpperCase()
                const p = item.params || []

                switch (cmd) {
                    case '^XA':
                        this._flushField()
                        break

                    case '^XZ':
                        this._flushField()
                        break

                    case '^LH': {
                        const x = this._toInt(p[0], 0),
                            y = this._toInt(p[1], 0)
                        this.state.labelHome = { x, y }
                        break
                    }

                    case '^PW': {
                        this.state.pw = this._toInt(p[0], null)
                        break
                    }

                    case '^LL': {
                        this.state.ll = this._toInt(p[0], null)
                        break
                    }

                    case '^CI': {
                        const set = this._toInt(p[0], 28)
                        this.state.charset = set === 28 ? 'utf-8' : 'ascii'
                        break
                    }

                    case '^FH': {
                        this.state.hexEscape = p[0] || null
                        this._ensureField()
                        this.state.field.fhEscape = this.state.hexEscape
                        break
                    }

                    case '^FO': {
                        this._flushField()
                        const x = this._toInt(p[0], 0),
                            y = this._toInt(p[1], 0)
                        this.state.currentPosition = { x, y }
                        this._ensureField()
                        Object.assign(this.state.field, { originMode: 'FO', x, y })
                        break
                    }

                    case '^FT': {
                        this._flushField()
                        const x = this._toInt(p[0], 0),
                            y = this._toInt(p[1], 0)
                        this.state.currentPosition = { x, y }
                        this._ensureField()
                        Object.assign(this.state.field, { originMode: 'FT', x, y })
                        break
                    }

                    case '^A': {
                        this._ensureField()
                        const fw = p[0] || '0N'
                        const name = fw.slice(0, -1) || '0'
                        const orient = fw.slice(-1) || 'N'
                        const h = this._toInt(p[1], this.state.font.h)
                        const w = this._toInt(p[2], h)
                        this.state.currentOrientation = orient
                        this.state.field.font = { name, orient, h, w }
                        break
                    }

                    case '^FB': {
                        this._ensureField()
                        const maxWidth = this._toInt(p[0], 0)
                        const maxLines = this._toInt(p[1], 1)
                        const lineSpacing = this._toInt(p[2], 0)
                        const alignment = (p[3] || 'L').toUpperCase()
                        const indent = this._toInt(p[4], 0)
                        this.state.field.fb = { maxWidth, maxLines, lineSpacing, alignment, indent }
                        break
                    }

                    case '^BQ': {
                        // BQ: QR Code
                        // Sintaxis: ^BQN,model,magnification,errorCorrectionLevel,maskValue
                        // N = orientación (N, R, I, B)
                        // model = 2 (Model 2 es el estándar)
                        // magnification = factor de escala (1-10)
                        // errorCorrectionLevel = L, M, Q, H
                        // maskValue = 0-7 (opcional)
                        this._ensureField()
                        const orient = (p[0] || 'N').toUpperCase()
                        const model = this._toInt(p[1], 2)
                        const magnification = this._toInt(p[2], 2)
                        const errorLevel = (p[3] || 'M').toUpperCase()
                        const maskValue = this._toInt(p[4], 0)

                        this.state.field.type = 'qrcode'
                        this.state.field.qrcode = {
                            orient,
                            model,
                            magnification,
                            errorLevel,
                            maskValue
                        }
                        // Actualizar orientación actual
                        this.state.currentOrientation = orient
                        break
                    }

                    case '^FD': {
                        this._ensureField()
                        const raw = p[0] !== undefined && p[0] !== null ? String(p[0]) : ''
                        const resolved = this.resolveFDText(raw)

                        if (this.state.field.type === 'qrcode') {
                            // Para QR code, almacenar el texto directamente
                            this.state.field.text = resolved
                            this.state.field.haveData = true
                        } else {
                            // Para texto normal, concatenar
                            this.state.field.text = (this.state.field.text || '') + resolved
                            this.state.field.haveData = true
                        }
                        break
                    }

                    case '^GF': {
                        const format = (p[0] || 'A').toUpperCase()
                        const dataBytes = this._toInt(p[1], 0)
                        const rowBytes = clampGfRowBytes(this._toInt(p[3], 0))
                        const totalBytes = clampGfTotalBytes(this._toInt(p[2], dataBytes))
                        const data = p[4] || ''

                        if (rowBytes <= 0 || totalBytes <= 0) break

                        let prepared
                        try {
                            prepared = prepareGfDecode(rowBytes, totalBytes)
                        } catch (err) {
                            const msg = err && err.message ? err.message : String(err)
                            this.validationErrors.push(msg)
                            break
                        }

                        this._ensureField()
                        Object.assign(this.state.field, {
                            type: 'image',
                            image: {
                                format,
                                dataBytes,
                                totalBytes: prepared.totalBytes,
                                rowBytes: prepared.rowBytes,
                                data,
                                width: prepared.width,
                                height: prepared.height
                            }
                        })

                        await this._drawGFField(this.state.field)
                        break
                    }

                    case '^GB': {
                        // GB siempre usa currentPosition (establecido por ^FO o ^FT)
                        // En ZPL, ^GB dibuja desde la posición actual establecida por ^FO o ^FT
                        // No usar la posición del campo porque puede tener offsets que no aplican a GB
                        const x = this.state.currentPosition.x || 0
                        const y = this.state.currentPosition.y || 0
                        const orient = this.state.currentOrientation || 'N'

                        const width = this._toInt(p[0], 0)
                        const height = this._toInt(p[1], 0)
                        const thickness = this._toInt(p[2], 1)
                        const color = p[3] || 'B'
                        const cornerRadius = this._toInt(p[4], 0)

                        this._drawGBBox(x, y, width, height, thickness, color, cornerRadius, orient)
                        break
                    }

                    case '^FR': {
                        // Activar modo reverse (texto en blanco) solo para el siguiente ^FD
                        // Asegurar que el campo existe y aplicar reverse directamente
                        this._ensureField()
                        if (this.state.field) {
                            this.state.field.reverse = true
                        }
                        // También marcar como pendiente para asegurar que se aplique si el campo se recrea
                        this.state.reverse = true
                        break
                    }

                    case '^FS':
                        this._flushField()
                        break

                    // no-ops visuales de momento:
                    case '~TA':
                    case '~JS':
                    case '^LT':
                    case '^MN':
                    case '^MT':
                    case '^PO':
                    case '^PM':
                    case '^JM':
                    case '^PR':
                    case '~SD':
                    case '^JU':
                    case '^MM':
                    case '^LS':
                    case '^PQ':
                        break

                    default:
                        // ignoramos comandos desconocidos
                        break
                }
            }
        },

        zoomIn() {
            if (this.zoomLevel < 3) this.zoomLevel = Math.min(3, this.zoomLevel + 0.25)
        },

        zoomOut() {
            if (this.zoomLevel > 0.5) this.zoomLevel = Math.max(0.5, this.zoomLevel - 0.25)
        },

        resetZoom() {
            this.zoomLevel = 1
        },

        exportToBase64() {
            if (!this.canvas) return

            try {
                const dataURL = this.canvas.toDataURL('image/png')
                this.$emit('export', dataURL)
                this.downloadImage(dataURL)
            } catch (e) {
                logError('Error al exportar imagen:', e)
            }
        },

        downloadImage(dataURL) {
            const link = document.createElement('a')
            link.download = 'zpl-preview.png'
            link.href = dataURL
            link.click()
        }
    }
}
</script>

<style scoped>
.zpl-preview {
    display: flex;
    flex-direction: column;
    gap: 1rem;
    width: 100%;
    min-width: 0;
    min-height: 0;
    flex: 0 0 auto;
    overflow: visible;
    align-items: center;
}

.zpl-controls {
    display: flex;
    align-items: center;
    gap: 0.5rem;
    padding: 0.5rem;
    background: #f5f5f5;
    border-radius: 8px;
    border: 1px solid #ddd;
}

.control-btn {
    border-radius: 6px;
    cursor: pointer;
    font-size: 14px;
    transition: all 0.2s;
    background: #053186;
    border: 1px solid #053186;
}

.control-btn:hover:not(:disabled) {
    background: transparent;
    color: #053186;
}

.control-btn:disabled {
    opacity: 0.5;
    cursor: not-allowed;
}

.zoom-level {
    min-width: 60px;
    text-align: center;
    font-weight: bold;
}

.zpl-preview-container {
    width: 100%;
    flex: 0 0 auto;
    overflow: hidden;
    border: 1px solid #ccc;
    background: #f9f9f9;
    border-radius: 8px;
    box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
}

.canvas-wrapper {
    padding: 0;
    display: inline-block;
    line-height: 0;
}

.zpl-canvas {
    display: block;
    background: white;
    border: 1px solid #ddd;
    box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
}

.validation-errors {
    background: #fff3cd;
    border: 1px solid #ffeaa7;
    border-radius: 8px;
    padding: 1rem;
    margin-top: 1rem;
}

.validation-errors h4 {
    margin: 0 0 0.5rem 0;
    color: #856404;
}

.error-item {
    color: #721c24;
    margin: 0.25rem 0;
    font-size: 0.9rem;
}
</style>
